<template>
  <div class="box">
    <div class="headline">
      <div class="top">Step 4 of 5</div>
        API endpoints and permissions
    </div>

    <b-form-group description="Expose schema via OpenAPI and include Swagger UI for it. DRF browsable API is always enabled.">
      <b-form-checkbox v-model="useOpenApi">
        Enable OpenAPI
        (<code>/api/v1/schema/</code> and <code>/api/v1/swagger-ui/</code>)
      </b-form-checkbox>
    </b-form-group>

    <b-form v-for="ep in endpoints" :key="ep.uri" class="model-endpoint">
      <b-form-checkbox v-model="ep.enabled" :disabled="!ep.model.serializer">
        <span>{{ep.app.name}}.<strong>{{ ep.model.name }}</strong></span>
        <small v-if="!ep.model.serializer" class="text-danger">(no fields included in serializer)</small>
        <code v-else>/api/v1/{{ ep.app.name }}/{{ ep.uri }}/</code>
      </b-form-checkbox>
      <b-row>
        <b-col cols="12" sm="6" md="3">
          <b-form-group label="Create">
            <b-form-select v-model="ep.allowCreate" :options="createPermissionsList" size="sm" :disabled="!ep.enabled"></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6" md="3">
          <b-form-group label="Read/List">
            <b-form-select v-model="ep.allowRead" :options="permissionsList(ep)" size="sm" :disabled="!ep.enabled"></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6" md="3">
          <b-form-group label="Update">
            <b-form-select v-model="ep.allowUpdate" :options="permissionsList(ep)" size="sm" :disabled="!ep.enabled"></b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6" md="3">
          <b-form-group label="Delete">
            <b-form-select v-model="ep.allowDelete" :options="permissionsList(ep)" size="sm" :disabled="!ep.enabled"></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
    <p class="text-danger text-center" v-if="error">
      At least one endpoint should be enabled for the REST service to be useful.
    </p>
    <b-form>
      <h4 class="mt-2">Rate-limiting</h4>
      <b-form-checkbox v-model="throttling.enabled">
          Enable rate limiting (throttling of incoming requests)
        </b-form-checkbox>

      <b-row v-if="throttling.enabled" class="mt-2">
        <b-col cols="12" md="6">
          <b-form-group
            v-if="throttling.enabled"
            md-label-cols="2"
            label="Anonymous"
            description="Limit anonymous requests (from single IP) to this number per hour"
          >
            <b-form-input v-model="throttling.anon" placeholder="example: 100"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group
            v-if="throttling.enabled"
            md-label-cols="2"
            label="Authennticated"
            description="Limit requests from authenticated users (per user) to this number per hour"
          >
            <b-form-input v-model="throttling.auth" placeholder="example: 1000"></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
    <b-form>
      <div class="button-row">
        <b-button @click="$emit('prev', exportData)">&larr; Prev</b-button>
        <b-button variant="primary" @click="onNext">Next &rarr;</b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import { API_PERMISSION_OPTIONS } from '../../helpers/constants'

const permsWithout = (exclude) => {
  const p = {}
  for (let perm of Object.keys(API_PERMISSION_OPTIONS)) {
    if (!exclude.includes(perm)) p[perm] = API_PERMISSION_OPTIONS[perm]
  }
  return p
}

export default {
  name: 'Api',

  props: {
    recipe: {type: Object, required: true}
  },

  data () {
    const useAuth = !!this.recipe.auth
    const defaultCreate = useAuth ? 'auth' : 'any'
    const defaultRead = m => m.owner_field ? 'owner' : (useAuth ? 'auth' : 'any')
    const defaultUpdate = m => m.owner_field ? 'owner' : (useAuth ? 'admin' : 'none')
    const defaultDelete = m => m.owner_field ? 'owner' : (useAuth ? 'admin' : 'none')

    const endpoints = []
    for (let app of this.recipe.apps) {
      for (let m of app.models) {
        endpoints.push({
          enabled: !!m.viewset && !!m.serializer,
          uri: m.name.toLowerCase(),
          model: m,
          app: app,
          allowCreate: m.viewset ? m.viewset.permissions.create : defaultCreate,
          allowRead: m.viewset ? m.viewset.permissions.read : defaultRead(m),
          allowUpdate: m.viewset ? m.viewset.permissions.update : defaultUpdate(m),
          allowDelete: m.viewset ? m.viewset.permissions.delete : defaultDelete(m)
        })
      }
    }

    const throttling = {
      enabled: false,
      anon: null,
      auth: null
    }

    if (this.recipe.throttling) {
      throttling.enabled = true
      throttling.anon = this.recipe.throttling.anonymous || null
      throttling.auth = this.recipe.throttling.authenticated || null
    }

    return {
      error: false,
      useOpenApi: !!this.recipe.use_openapi,
      endpoints,
      throttling
    }
  },

  computed: {
    useAuth () {
      return !!this.recipe.auth
    },

    createPermissionsList () {
      return permsWithout(this.useAuth ? ['owner'] : ['owner', 'auth'])
    },

    exportData () {
      const data = {}

      for (let ep of this.endpoints) {
        if (ep.enabled) {
          data[`${ep.app.name}.${ep.model.name}`] = {
            permissions: {
              create: ep.allowCreate,
              read: ep.allowRead,
              update: ep.allowUpdate,
              'delete': ep.allowDelete
            }
          }
        }
      }

      let throttling = null
      if (this.throttling.enabled && (+this.throttling.anon || +this.throttling.auth)) {
        throttling = {
          anonymous: +this.throttling.anon || null,
          authenticated: +this.throttling.auth || null
        }
      }

      return {
        useOpenApi: this.useOpenApi,
        viewsets: data,
        throttling
      }
    }
  },

  methods: {
    permissionsList (ep) {
      if (!this.useAuth) return permsWithout(['owner', 'auth'])
      return permsWithout(!ep.model.owner_field ? ['owner'] : [])
    },

    validate () {
      if (Object.keys(this.exportData).length === 0) {
        this.error = true
        return false
      }

      return true
    },

    onNext () {
      if (!this.validate()) return

      this.$emit('next', this.exportData)
    }
  }
}
</script>

<style scoped lang="scss">

.model-endpoint {
  border-top: 1px solid #ccc;
  padding-top: 0.25em;
  margin-top: 0.25em;
}
</style>



// WEBPACK FOOTER //
// src/components/wizard/Api.vue